const caseStudies = [
  {
    id: 'case-fallback',
    brand: 'Mindify',
    brandColor: '#6C47FF',
    title: 'Case Study',
    description: 'Add case videos and links in the Shopify theme editor.',
    videoUrl: '',
    externalVideoId: '',
    externalVideoType: '',
    ctaLink: '/pages/cases',
    ctaText: 'View Cases'
  }
];

const MindifyCaseVideo = {
  pageReady: document.readyState === 'complete',

  markPageReady() {
    this.pageReady = true;
    document.dispatchEvent(new CustomEvent('mindify:page-video-ready'));
  },

  loadMedia(media) {
    if (!media) return;
    const slide = media.closest('.case-slide, .mindify-case-inline-video');
    if (slide) slide.classList.add('is-loading');

    if (media.tagName === 'IFRAME') {
      if (!media.src && media.dataset.src) media.src = media.dataset.src;
      media.addEventListener('load', () => {
        if (slide) {
          slide.classList.remove('is-loading');
          slide.classList.add('has-playing-video');
        }
      }, { once: true });
      return;
    }

    if (media.tagName === 'VIDEO') {
      media.muted = true;
      media.loop = true;
      media.playsInline = true;
      media.autoplay = true;
      media.preload = 'metadata';
      if (!media.currentSrc && !media.src && media.dataset.src) {
        media.src = media.dataset.src;
        media.load();
      }
    }
  },

  playMedia(media) {
    if (!media || media.tagName !== 'VIDEO') return;
    this.loadMedia(media);
    const slide = media.closest('.case-slide, .mindify-case-inline-video');
    const playPromise = media.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => {
        if (slide) {
          slide.classList.remove('is-loading');
          slide.classList.add('has-playing-video');
        }
      }).catch(() => {
        if (slide) slide.classList.remove('is-loading');
      });
    }
  },

  pauseMedia(media) {
    if (media && media.tagName === 'VIDEO') media.pause();
  }
};

if (document.readyState === 'complete') {
  MindifyCaseVideo.pageReady = true;
} else {
  window.addEventListener('load', () => MindifyCaseVideo.markPageReady(), { once: true });
}

class CaseShowcase {
  constructor(container, cases) {
    this.container = container;
    this.cases = cases;
    this.swiper = null;
    this.isInViewport = false;
    this.viewportObserver = null;
    this.init();
  }

  init() {
    this.renderSlides();
    this.initSwiper();
    this.bindEvents();
    this.observeViewport();
  }

  renderSlides() {
    const wrapper = this.container.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    wrapper.innerHTML = this.cases.map((caseData, index) => {
      const externalEmbed = this.getExternalEmbedUrl(caseData);
      const hasLocalVideo = Boolean(caseData.videoUrl);
      return `
        <div class="swiper-slide case-slide" data-index="${index}">
          ${externalEmbed ? `
            <iframe
              class="case-slide__video case-slide__embed"
              data-src="${externalEmbed}"
              title="${this.escape(caseData.brand || 'Case video')}"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowfullscreen
              loading="lazy"
            ></iframe>
          ` : hasLocalVideo ? `
            <video
              class="case-slide__video"
              data-src="${this.escape(caseData.videoUrl)}"
              muted
              loop
              playsinline
              preload="none"
            ></video>
          ` : `
            <div class="case-slide__empty">Add a video in the Shopify editor.</div>
          `}

          ${(externalEmbed || hasLocalVideo) ? `
            <div class="case-slide__loading" aria-hidden="true">
              <span></span>
              <strong>Loading video</strong>
            </div>
          ` : ''}

          <div class="case-slide__overlay">
            <div class="case-slide__brand" style="color: ${this.escape(caseData.brandColor || '#6C47FF')};">${this.escape(caseData.brand || '')}</div>
            <h3 class="case-slide__title">${this.escape(caseData.title || '')}</h3>
            <p class="case-slide__description">${this.escape(caseData.description || '')}</p>
            <a href="${this.escape(caseData.ctaLink || '/pages/cases')}" class="case-slide__cta">
              ${this.escape(caseData.ctaText || 'View Case')}
              <i class="fa-solid fa-arrow-right text-xs"></i>
            </a>
          </div>
        </div>
      `;
    }).join('');
  }

  escape(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  getExternalEmbedUrl(caseData) {
    if (!caseData || !caseData.externalVideoId || !caseData.externalVideoType) return '';
    if (caseData.externalVideoType === 'youtube') {
      return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(caseData.externalVideoId)}?autoplay=1&mute=1&loop=1&playlist=${encodeURIComponent(caseData.externalVideoId)}&controls=0&playsinline=1&rel=0`;
    }
    if (caseData.externalVideoType === 'vimeo') {
      return `https://player.vimeo.com/video/${encodeURIComponent(caseData.externalVideoId)}?autoplay=1&muted=1&loop=1&background=1`;
    }
    return '';
  }

  initSwiper() {
    this.swiper = new Swiper(this.container.querySelector('.swiper'), {
      loop: this.cases.length > 1,
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      },
      speed: 800,
      pagination: {
        el: this.container.querySelector('.case-showcase__pagination'),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active'
      },
      navigation: {
        prevEl: this.container.querySelector('.case-showcase__arrow--prev'),
        nextEl: this.container.querySelector('.case-showcase__arrow--next')
      },
      on: {
        init: () => this.updateActiveVideo(),
        slideChangeTransitionStart: () => this.pauseAllVideos(),
        slideChangeTransitionEnd: () => this.updateActiveVideo()
      }
    });
    if (this.swiper.autoplay) this.swiper.autoplay.stop();
  }

  bindEvents() {
    const playPauseBtn = this.container.querySelector('.case-showcase__play-pause');
    if (playPauseBtn) playPauseBtn.style.display = 'none';

    document.addEventListener('mindify:page-video-ready', () => this.updateActiveVideo());

    this.container.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      this.updateActiveVideo(true);
    });
  }

  observeViewport() {
    const enter = () => {
      this.isInViewport = true;
      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.start();
      this.updateActiveVideo();
    };
    const leave = () => {
      this.isInViewport = false;
      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.stop();
      this.pauseAllVideos();
    };

    if (!('IntersectionObserver' in window)) {
      enter();
      return;
    }

    this.viewportObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.15) enter();
        else leave();
      });
    }, { threshold: [0, 0.15, 0.4], rootMargin: '140px 0px' });

    this.viewportObserver.observe(this.container);
  }

  getActiveMedia() {
    if (!this.swiper || !this.swiper.slides) return null;
    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    return activeSlide ? activeSlide.querySelector('.case-slide__video') : null;
  }

  updateActiveVideo(force = false) {
    if (!this.isInViewport || (!MindifyCaseVideo.pageReady && !force)) return;
    const media = this.getActiveMedia();
    if (!media) return;
    if (media.tagName === 'IFRAME') MindifyCaseVideo.loadMedia(media);
    else MindifyCaseVideo.playMedia(media);
  }

  pauseAllVideos() {
    this.container.querySelectorAll('video.case-slide__video').forEach((video) => {
      MindifyCaseVideo.pauseMedia(video);
    });
  }

  destroy() {
    this.pauseAllVideos();
    if (this.swiper) this.swiper.destroy(true, true);
    if (this.viewportObserver) this.viewportObserver.disconnect();
  }
}

function initCaseShowcases(root = document) {
  root.querySelectorAll('.case-showcase').forEach((showcase) => {
    if (showcase.caseShowcaseInstance) {
      showcase.caseShowcaseInstance.destroy();
      showcase.caseShowcaseInstance = null;
    }
    const editableCaseStudies = Array.isArray(window.CASE_DATA) && window.CASE_DATA.length ? window.CASE_DATA : caseStudies;
    if (showcase.querySelector('.swiper')) {
      showcase.caseShowcaseInstance = new CaseShowcase(showcase, editableCaseStudies);
    }
  });
  initInlineCaseAutoplay(root);
}

function initInlineCaseAutoplay(root = document) {
  root.querySelectorAll('.mindify-case-inline-video video').forEach((video) => {
    const src = video.getAttribute('src') || video.currentSrc || video.getAttribute('data-src');
    if (!src) return;
    if (!video.getAttribute('src')) video.setAttribute('src', src);
    video.classList.remove('mindify-deferred-video');
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'metadata';
    const shell = video.closest('.mindify-case-inline-video');
    if (shell) {
      shell.classList.remove('is-idle', 'is-loading');
      shell.classList.add('has-playing-video');
      const loading = shell.querySelector('.mindify-video-loading');
      if (loading) loading.remove();
    }
    if (video.readyState === 0) {
      try { video.load(); } catch (error) {}
    }
    const play = video.play();
    if (play && typeof play.catch === 'function') play.catch(() => {});
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCaseShowcases(document);
  setTimeout(() => initInlineCaseAutoplay(document), 1000);
  setTimeout(() => initInlineCaseAutoplay(document), 3000);
});
document.addEventListener('shopify:section:load', (event) => {
  initCaseShowcases(event.target);
  initInlineCaseAutoplay(event.target);
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CaseShowcase, MindifyCaseVideo, caseStudies, initInlineCaseAutoplay };
}
